/**
 * Pilot şablon: Aylık Banka Raporu (docs/REPORT-DOCUMENTS-PLAN.md §4.4; antet bileşendir,
 * içerikte yok). D6/D9'da editörde başlangıç şablonu olarak kullanılır.
 *
 * Ölçüler D9.1/D9.2'de Cube'a eklendi: hareketler `BankaHareketleri`, dönem sonu bakiyesi ayrı
 * view (`BankaDonemSonuBakiyeleri`; sayfa dönem filtresinden yalnız üst sınırı alır).
 */

const VIEW = 'BankaHareketleri'
const BALANCE_VIEW = 'BankaDonemSonuBakiyeleri'
const TL = [{ member: 'doviz', values: ['TL'] }]
const USD = [{ member: 'doviz', values: ['USD'] }]

const text = (value: string, bold = false) => ({
  type: 'text',
  text: value,
  ...(bold ? { marks: [{ type: 'bold' }] } : {}),
})
const value = (of: string) => ({ type: 'value', attrs: { of } })
const change = (of: string) => ({ type: 'change', attrs: { of } })
const paragraph = (...content: object[]) => ({ type: 'paragraph', content })
const heading = (level: number, ...content: object[]) => ({
  type: 'heading',
  attrs: { level },
  content,
})
const kpi = (measure: string, title: string) => ({
  type: 'reportKpi',
  attrs: { view: VIEW, measure, title, span: 3 },
})
const moneyTable = (
  title: string,
  dimensions: string[],
  measures: string[],
  where = TL,
  view = VIEW,
) => ({
  type: 'reportTable',
  attrs: { view, title, dimensions, measures, where, totals: true },
})

export const monthlyBankReport = {
  kind: 'document',
  title: 'Aylık Banka Raporu',
  orientation: 'portrait',
  filters: [
    { view: VIEW, member: 'tarih', type: 'period', granularity: 'month', default: '-1m' },
    { view: VIEW, member: 'firma', type: 'multiSelect' },
  ],
  queries: {
    ozet: {
      view: VIEW,
      measures: ['girisTL', 'cikisTL', 'netTL', 'count'],
      compare: 'previous',
    },
    firmalar: {
      view: VIEW,
      dimensions: ['firma'],
      measures: ['girisTL', 'cikisTL', 'netTL'],
      order: ['-girisTL'],
    },
    // Dönem sonu bakiyesi: hesap başına dönem sonuna kadarki son hareketin `bakiye`'si.
    donemSonu: { view: BALANCE_VIEW, measures: ['donemSonuTL', 'donemSonuUSD'] },
  },
  content: {
    type: 'doc',
    content: [
      heading(1, text('Aylık Banka Raporu — '), value('period.label')),
      paragraph(text('Firmalar: '), value('filters.firma')),
      kpi('girisTL', 'TL giriş'),
      kpi('cikisTL', 'TL çıkış'),
      kpi('netTL', 'TL net'),
      kpi('count', 'Hareket sayısı'),
      paragraph(
        text('Dönem içinde TL hesaplara toplam '),
        value('ozet.girisTL'),
        text(' giriş, '),
        value('ozet.cikisTL'),
        text(' çıkış oldu; net '),
        value('ozet.netTL'),
        text('. Girişler önceki aya göre '),
        change('ozet.girisTL'),
        text('.'),
      ),
      {
        type: 'condition',
        attrs: { left: 'ozet.netTL', op: 'gt', right: 0 },
        content: [
          {
            type: 'conditionThen',
            content: [paragraph(text('Dönem net nakit girişiyle kapandı.'))],
          },
          {
            type: 'conditionElse',
            content: [paragraph(text('Dönem net nakit çıkışıyla kapandı.'))],
          },
        ],
      },
      {
        type: 'reportChart',
        attrs: {
          view: VIEW,
          title: 'Son 12 ay TL giriş ve çıkış',
          time: 'tarih',
          granularity: 'month',
          range: '-12m',
          ignoreFilters: ['tarih'],
          series: [
            { measure: 'girisTL', type: 'bar' },
            { measure: 'cikisTL', type: 'bar' },
            { measure: 'netTL', type: 'line' },
          ],
        },
      },
      moneyTable(
        'TL hareketleri (firma ve banka)',
        ['firma', 'banka'],
        ['girisTL', 'cikisTL', 'netTL'],
      ),
      moneyTable(
        'USD hareketleri (firma ve banka)',
        ['firma', 'banka'],
        ['girisUSD', 'cikisUSD', 'netUSD'],
        USD,
      ),
      heading(2, text('Dönem sonu bakiyeleri')),
      paragraph(
        text('Dönem sonu toplam bakiye: '),
        value('donemSonu.donemSonuTL'),
        text(' (TL), '),
        value('donemSonu.donemSonuUSD'),
        text(' (USD).'),
      ),
      moneyTable(
        'TL hesaplar',
        ['firma', 'banka', 'hesapNo'],
        ['donemSonuBakiye'],
        TL,
        BALANCE_VIEW,
      ),
      moneyTable(
        'USD hesaplar',
        ['firma', 'banka', 'hesapNo'],
        ['donemSonuBakiye'],
        USD,
        BALANCE_VIEW,
      ),
      { type: 'pagebreak' },
      {
        type: 'repeat',
        attrs: { over: 'firmalar', limit: 50 },
        content: [
          heading(3, value('row.firma')),
          paragraph(
            text('Dönem girişi '),
            value('row.girisTL'),
            text(', çıkışı '),
            value('row.cikisTL'),
            text(', net '),
            value('row.netTL'),
            text('.'),
          ),
          moneyTable('İşlem türüne göre TL', ['islemTuru'], ['girisTL', 'cikisTL', 'netTL']),
          {
            type: 'reportList',
            attrs: {
              view: VIEW,
              title: 'En büyük 10 TL giriş',
              columns: ['tarih', 'banka', 'gondericiHesapAdi', 'aciklama', 'tutar'],
              rowKey: 'key',
              defaultSort: ['-tutar'],
              where: TL,
              limit: 10,
            },
          },
        ],
      },
    ],
  },
}
